import { useState, useEffect } from 'react'
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Maximize,
  List
} from 'lucide-react'

/**
 * MediaControls - Admin Control Panel for Media Playback
 *
 * Features:
 * - Play/Pause button
 * - Skip Previous/Next buttons
 * - Seek bar (draggable progress bar)
 * - Fullscreen toggle
 * - Queue panel toggle
 * - Current time / Duration display
 *
 * Only visible to users with canControl permission
 * Updated to use new database schema (platform, playback_position, state)
 *
 * @param {Object} session - Current media session (new schema)
 * @param {Function} onPlay - Play callback
 * @param {Function} onPause - Pause callback
 * @param {Function} onSeek - Seek callback (time in seconds)
 * @param {Function} onPrevious - Skip to previous item
 * @param {Function} onNext - Skip to next item
 * @param {Function} onToggleFullscreen - Toggle fullscreen
 * @param {Function} onToggleQueue - Toggle queue panel
 * @param {Number} queueCount - Number of items in queue
 * @param {Boolean} showQueue - Whether queue is visible
 */
export default function MediaControls({
  session,
  onPlay,
  onPause,
  onSeek,
  onPrevious,
  onNext,
  onToggleFullscreen,
  onToggleQueue,
  queueCount = 0,
  showQueue = false
}) {
  const [isSeeking, setIsSeeking] = useState(false)
  const [seekValue, setSeekValue] = useState(0)

  // Helper: Check if playing
  const isPlaying = session?.state === 'playing'

  // Update seek value when session time changes (unless user is actively seeking)
  useEffect(() => {
    if (!isSeeking && session) {
      const progress = session.duration
        ? ((session.playback_position || 0) / session.duration) * 100
        : 0
      setSeekValue(progress)
    }
  }, [session?.playback_position, session?.duration, isSeeking])

  // Format time (seconds to MM:SS)
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Handle seek bar change
  const handleSeekChange = (e) => {
    const value = parseFloat(e.target.value)
    setSeekValue(value)
  }

  // Handle seek start (mouse down)
  const handleSeekStart = () => {
    setIsSeeking(true)
  }

  // Handle seek end (mouse up)
  const handleSeekEnd = (e) => {
    setIsSeeking(false)
    const value = parseFloat(e.target.value)
    if (session?.duration) {
      const time = (value / 100) * session.duration
      onSeek?.(time)
    }
  }

  // Handle play/pause toggle
  const handlePlayPause = () => {
    if (isPlaying) {
      onPause?.()
    } else {
      onPlay?.()
    }
  }

  if (!session) return null

  return (
    <div className="space-y-3">
      {/* Progress Bar */}
      <div className="space-y-1">
        <input
          type="range"
          min="0"
          max="100"
          step="0.1"
          value={seekValue}
          onChange={handleSeekChange}
          onMouseDown={handleSeekStart}
          onMouseUp={handleSeekEnd}
          onTouchStart={handleSeekStart}
          onTouchEnd={handleSeekEnd}
          className="w-full h-2 rounded-full appearance-none cursor-pointer
                     bg-gjyl-slate-700
                     [&::-webkit-slider-thumb]:appearance-none
                     [&::-webkit-slider-thumb]:w-4
                     [&::-webkit-slider-thumb]:h-4
                     [&::-webkit-slider-thumb]:rounded-full
                     [&::-webkit-slider-thumb]:bg-gjyl-indigo-500
                     [&::-webkit-slider-thumb]:cursor-pointer
                     [&::-webkit-slider-thumb]:transition-all
                     [&::-webkit-slider-thumb]:hover:scale-110
                     [&::-moz-range-thumb]:w-4
                     [&::-moz-range-thumb]:h-4
                     [&::-moz-range-thumb]:rounded-full
                     [&::-moz-range-thumb]:bg-gjyl-indigo-500
                     [&::-moz-range-thumb]:border-0
                     [&::-moz-range-thumb]:cursor-pointer
                     [&::-moz-range-thumb]:transition-all
                     [&::-moz-range-thumb]:hover:scale-110"
          style={{
            background: `linear-gradient(to right,
              rgb(99, 102, 241) 0%,
              rgb(99, 102, 241) ${seekValue}%,
              rgb(51, 65, 85) ${seekValue}%,
              rgb(51, 65, 85) 100%)`
          }}
        />

        {/* Time Display */}
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>{formatTime(session.playback_position || 0)}</span>
          <span>{formatTime(session.duration)}</span>
        </div>
      </div>

      {/* Control Buttons */}
      <div className="flex items-center justify-between">
        {/* Left: Playback Controls */}
        <div className="flex items-center gap-2">
          {/* Previous */}
          <button
            onClick={onPrevious}
            className="btn-ghost p-2 rounded-full hover:bg-gjyl-slate-700 transition-all"
            title="Previous"
          >
            <SkipBack className="w-5 h-5" />
          </button>

          {/* Play/Pause */}
          <button
            onClick={handlePlayPause}
            className="btn-primary p-3 rounded-full hover:scale-105 active:scale-95 transition-all shadow-gjyl-glow"
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <Pause className="w-6 h-6" fill="currentColor" />
            ) : (
              <Play className="w-6 h-6" fill="currentColor" />
            )}
          </button>

          {/* Next */}
          <button
            onClick={onNext}
            className="btn-ghost p-2 rounded-full hover:bg-gjyl-slate-700 transition-all"
            title="Next"
          >
            <SkipForward className="w-5 h-5" />
          </button>
        </div>

        {/* Right: Fullscreen, Queue */}
        <div className="flex items-center gap-4">
          {/* Fullscreen */}
          <button
            onClick={onToggleFullscreen}
            className="btn-ghost p-2 rounded-lg hover:bg-gjyl-slate-700 transition-all"
            title="Fullscreen"
          >
            <Maximize className="w-5 h-5" />
          </button>

          {/* Queue Toggle */}
          <button
            onClick={onToggleQueue}
            className={`btn-ghost p-2 rounded-lg hover:bg-gjyl-slate-700 transition-all relative ${
              showQueue ? 'bg-gjyl-slate-700' : ''
            }`}
            title="Toggle Queue"
          >
            <List className="w-5 h-5" />
            {queueCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-gjyl-indigo-500 rounded-full text-xs flex items-center justify-center font-semibold">
                {queueCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Media Info (mobile-friendly) */}
      <div className="glass rounded-lg p-3 flex items-center gap-3 lg:hidden">
        {session.thumbnail_url && (
          <img
            src={session.thumbnail_url}
            alt=""
            className="w-12 h-12 rounded object-cover"
          />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">
            {session.title || session.video_url}
          </p>
          <p className="text-xs text-slate-400 capitalize">
            {session.platform}
          </p>
        </div>
      </div>
    </div>
  )
}
